/**
 * Main-thread .NET WASM host (same model as wasmbrowser / CSharp-In-Browser).
 * Do NOT boot dotnet inside a Web Worker — dotnet.create() can hang there with no logs.
 */
import type { CompileResult, ComputeResult, OhlcBar } from "../runtime-types";
import { getPlatformConfig } from "@/platform/platform-context";
import { resolveIndicatorUrl } from "@/platform/resolve-url";
import { appendCompileLogsToBuild } from "./append-compile-logs";
import { invalidateCustomIndicatorCompile } from "../compiled-template";
import { useIndicatorCompileStore } from "./compile-store";
import { useIndicatorLoggerStore } from "./logger-store";
import {
  listDotnetExportNames,
  resolveDotnetExport,
} from "./resolve-dotnet-export";

function getRuntimeBaseHref(): string {
  const config = getPlatformConfig();
  const path = resolveIndicatorUrl("/indicator-runtime/wwwroot/", config);
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path.endsWith("/") ? path : `${path}/`;
  }
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  return new URL(path, origin).href;
}

function getRuntimeVersionUrl(): string {
  return resolveIndicatorUrl(
    "/indicator-runtime/.runtime-version",
    getPlatformConfig(),
  );
}

type DotnetApi = {
  CompileProject: (json: string) => string;
  LoadCompiledProject: (json: string) => string;
  ComputeIndicator: (json: string) => string;
  runInit: (projectId: string, instanceId: string, inputValues: Record<string, unknown>) => string;
  runDeinit: (instanceId: string) => string;
};

let dotnetApi: DotnetApi | null = null;
let initPromise: Promise<void> | null = null;
let loadedRuntimeBuiltAt: string | null = null;
/** builtAt stamp exposed to .NET as __vybeManagedVersion (cache-buster for managed/*.dll). */
let runtimeBuiltAtForImports = "";

async function fetchRuntimeBuiltAt(): Promise<string> {
  if (typeof window === "undefined") return "";
  try {
    const res = await fetch(getRuntimeVersionUrl(), { cache: "no-store" });
    if (!res.ok) return "";
    const json = (await res.json()) as { builtAt?: string };
    return json.builtAt ?? "";
  } catch {
    return "";
  }
}

function dotnetJsUrl(builtAt: string): string {
  const base = `${getRuntimeBaseHref()}_framework/dotnet.js`;
  return builtAt ? `${base}?v=${encodeURIComponent(builtAt)}` : base;
}

function progress(message: string) {
  useIndicatorLoggerStore.getState().setRuntimeMessage(message);
}

function wireImports(
  setModuleImports: (name: string, mod: Record<string, unknown>) => void
) {
  setModuleImports("main.js", {
    __vybeIndicatorEmitBuildLog: (line: string) => {
      useIndicatorLoggerStore.getState().appendBuild(String(line));
    },
    __vybeIndicatorLoggerPrint: (message: string) => {
      useIndicatorLoggerStore.getState().appendLogger(String(message));
    },
    __vybeManagedBase: () => getRuntimeBaseHref(),
    __vybeManagedVersion: () => runtimeBuiltAtForImports,
  });
}

function buildDotnetApi(
  program: Record<string, unknown>,
  exports: Record<string, unknown>
): DotnetApi {
  const compileProject = resolveDotnetExport(program, exports, [
    "CompileProject",
  ]) as ((json: string) => string) | undefined;
  const loadCompiledProject = resolveDotnetExport(program, exports, [
    "LoadCompiledProject",
  ]) as ((json: string) => string) | undefined;
  const computeIndicator = resolveDotnetExport(program, exports, [
    "ComputeIndicator",
  ]) as ((json: string) => string) | undefined;

  if (!compileProject || !computeIndicator) {
    const names = listDotnetExportNames(program).join(", ") || "(none)";
    throw new Error(
      `Indicator WASM missing CompileProject/ComputeIndicator. Found: ${names}. Run: npm run build:indicator-runtime — then hard-refresh.`
    );
  }

  const initJson = resolveDotnetExport(program, exports, [
    "InitIndicatorJson",
  ]) as ((json: string) => string) | undefined;
  const deinitJson = resolveDotnetExport(program, exports, [
    "DeinitIndicatorJson",
  ]) as ((json: string) => string) | undefined;

  const runInit = (projectId: string, instanceId: string, inputValues: Record<string, unknown>) => {
    if (initJson) {
      return initJson(
        JSON.stringify({ projectId, instanceId, inputValues })
      );
    }
    return computeIndicator(
      JSON.stringify({
        projectId,
        instanceId,
        inputValues,
        bars: [],
        lifecycle: "init",
      })
    );
  };

  const runDeinit = (instanceId: string) => {
    if (deinitJson) {
      return deinitJson(JSON.stringify({ instanceId }));
    }
    return computeIndicator(
      JSON.stringify({
        projectId: "",
        instanceId,
        inputValues: {},
        bars: [],
        lifecycle: "deinit",
      })
    );
  };

  return {
    CompileProject: compileProject.bind(program),
    LoadCompiledProject: loadCompiledProject
      ? loadCompiledProject.bind(program)
      : () =>
          JSON.stringify({
            success: false,
            logs: [
              "LoadCompiledProject export missing — rebuild indicator-runtime and hard-refresh.",
            ],
            inputs: [],
            plots: [],
          } satisfies CompileResult),
    ComputeIndicator: computeIndicator.bind(program),
    runInit,
    runDeinit,
  };
}

async function bootRuntime(): Promise<void> {
  const builtAt = await fetchRuntimeBuiltAt();
  runtimeBuiltAtForImports = builtAt;
  if (loadedRuntimeBuiltAt && builtAt && loadedRuntimeBuiltAt !== builtAt) {
    throw new Error(
      "Indicator runtime was rebuilt. Hard-refresh the page (Cmd+Shift+R), then Compile again."
    );
  }

  progress("Loading .NET WebAssembly runtime…");
  const { dotnet } = await import(/* webpackIgnore: true */ dotnetJsUrl(builtAt));

  const isDev = import.meta.env.DEV;
  const builder = isDev ? dotnet.withDiagnosticTracing(true) : dotnet;

  progress("Booting WASM runtime…");
  const { setModuleImports, getAssemblyExports, getConfig, runMain } =
    await builder.create();

  wireImports(setModuleImports);
  progress("Starting .NET main…");
  await runMain();

  const config = getConfig();
  const exports = (await getAssemblyExports(
    config.mainAssemblyName
  )) as Record<string, unknown>;
  const program = (exports.Program ?? exports) as Record<string, unknown>;

  dotnetApi = buildDotnetApi(program, exports);

  progress("Loading compiler references…");
  const initialize = resolveDotnetExport(program, exports, ["Initialize"]) as
    | (() => Promise<void>)
    | undefined;
  if (initialize) {
    await initialize.call(program);
  }

  loadedRuntimeBuiltAt = builtAt || loadedRuntimeBuiltAt;

  const exportNames = listDotnetExportNames(program);
  const hasJsonLifecycle =
    exportNames.includes("InitIndicatorJson") &&
    exportNames.includes("DeinitIndicatorJson");

  const logger = useIndicatorLoggerStore.getState();
  logger.setRuntimeReady(true);
  logger.setRuntimeMessage(null);
  logger.appendBuild(
    `[Runtime] Ready${builtAt ? ` (built ${new Date(builtAt).toLocaleString()})` : ""}${hasJsonLifecycle ? " — InitIndicatorJson OK" : " — lifecycle via ComputeIndicator fallback"}.`
  );
}

/** builtAt stamp of the runtime currently loaded in this page, or null before boot. */
export function getLoadedRuntimeBuiltAt(): string | null {
  return loadedRuntimeBuiltAt;
}

export async function ensureIndicatorRuntimeHost(): Promise<void> {
  if (dotnetApi) return;
  if (!initPromise) {
    initPromise = bootRuntime().catch((e) => {
      initPromise = null;
      throw e;
    });
  }
  await initPromise;
}

export async function compileIndicatorOnHost(args: {
  projectId: string;
  csprojXml: string;
  files: Record<string, string>;
}): Promise<CompileResult> {
  const logger = useIndicatorLoggerStore.getState();
  logger.setCompiling(true);
  logger.appendTerminal(`vybe@indicator:~$ dotnet build ${args.projectId}`);
  logger.appendBuild(`[Build] Build started (${args.files ? Object.keys(args.files).filter((p) => p.endsWith(".cs")).length : 0} .cs file(s))…`);

  try {
    await ensureIndicatorRuntimeHost();
    if (!dotnetApi) throw new Error("Indicator runtime not initialized");

    const raw = dotnetApi.CompileProject(
      JSON.stringify({
        projectId: args.projectId,
        csprojXml: args.csprojXml,
        files: args.files,
      })
    );
    const result = JSON.parse(raw) as CompileResult;
    appendCompileLogsToBuild(result);
    useIndicatorCompileStore.getState().setCompileResult(args.projectId, result);
    if (result.success) {
      invalidateCustomIndicatorCompile(args.projectId);
      logger.appendLogger("--- compile succeeded — indicator will reload ---");
      const dashCells = result.dashboard?.cells?.length ?? 0;
      logger.appendBuild(
        `[Build] OK — ${result.className ?? "indicator"} (${result.plots?.length ?? 0} plot(s), ${result.inputs?.length ?? 0} input(s)${dashCells > 0 ? `, ${dashCells} dashboard row(s)` : ""}).`
      );
      logger.appendTerminal("Build succeeded.");
    } else {
      const errCount = (result.logs ?? []).filter((l) =>
        /error\s+(CS\d+|:)/i.test(l)
      ).length;
      logger.appendBuild(
        `[Build] FAILED${errCount > 0 ? ` (${errCount} error line(s) above)` : ""}.`
      );
      logger.appendTerminal("Build failed.");
    }
    return result;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    logger.appendBuild(`[Build] ${message}`);
    logger.appendTerminal("Build failed.");
    throw e;
  } finally {
    logger.setCompiling(false);
  }
}

export async function loadCompiledOnHost(args: {
  projectId: string;
  assemblyBase64: string;
  csprojXml: string;
}): Promise<CompileResult> {
  const logger = useIndicatorLoggerStore.getState();
  logger.appendBuild(`[Build] Loading persisted assembly for ${args.projectId}…`);

  try {
    await ensureIndicatorRuntimeHost();
    if (!dotnetApi) throw new Error("Indicator runtime not initialized");

    const raw = dotnetApi.LoadCompiledProject(
      JSON.stringify({
        projectId: args.projectId,
        assemblyBase64: args.assemblyBase64,
        csprojXml: args.csprojXml,
      })
    );
    const result = JSON.parse(raw) as CompileResult;
    appendCompileLogsToBuild(result);
    useIndicatorCompileStore.getState().setCompileResult(args.projectId, result);
    if (result.success) {
      invalidateCustomIndicatorCompile(args.projectId);
      logger.appendBuild(
        `[Build] OK — loaded ${result.className ?? "indicator"} (${result.plots?.length ?? 0} plot(s)).`
      );
    } else {
      logger.appendBuild("[Build] FAILED to load persisted assembly.");
    }
    return result;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    logger.appendBuild(`[Build] ${message}`);
    throw e;
  }
}

export async function computeIndicatorOnHost(args: {
  projectId: string;
  instanceId: string;
  inputValues: Record<string, number | string | boolean>;
  bars: OhlcBar[];
  lifecycle: "init" | "calculate" | "deinit";
  mode?: "full" | "incremental";
  startIndex?: number;
}): Promise<ComputeResult> {
  await ensureIndicatorRuntimeHost();
  if (!dotnetApi) throw new Error("Indicator runtime not initialized");

  let raw: string;
  if (args.lifecycle === "deinit") {
    raw = dotnetApi.runDeinit(args.instanceId);
  } else if (args.lifecycle === "init") {
    raw = dotnetApi.runInit(
      args.projectId,
      args.instanceId,
      args.inputValues
    );
  } else {
    raw = dotnetApi.ComputeIndicator(JSON.stringify(args));
  }
  return JSON.parse(raw) as ComputeResult;
}

/** Warm up runtime on the main thread (chart mount or editor open). */
export function preloadIndicatorRuntimeHost(): void {
  if (typeof window === "undefined") return;
  void ensureIndicatorRuntimeHost().catch((e) => {
    const message = e instanceof Error ? e.message : String(e);
    const logger = useIndicatorLoggerStore.getState();
    logger.setRuntimeReady(false);
    logger.setRuntimeMessage(
      `Runtime failed: ${message}. Run "npm run build:indicator-runtime" from apps/web (or repo root), restart the dev server, and hard-refresh.`
    );
    logger.appendBuild(`[Runtime] ${message}`);
    console.error("[indicator-runtime]", e);
  });
}

